/* eslint global-require: off */
/* eslint no-use-before-define: off */
// Core
import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { compose, withHandlers, withProps, withState, lifecycle } from 'recompose'

// Helpers
import shallowEqual from 'shallow-equal/objects'
import _values from 'lodash/values'
import _mapValues from 'lodash/mapValues'
import _get from 'lodash/get'
import _sortBy from 'lodash/sortBy'
import _filter from 'lodash/filter'
import { interpolateRgb } from 'd3-interpolate'
import { select, event } from 'd3-selection'
import { format } from 'd3-format'
import { scaleLinear } from 'd3-scale'
import { legendColor } from 'd3-svg-legend'
import { geoPath } from 'd3-geo'
import { color as d3Color } from 'd3-color'
import { colors } from '../../theme/Global'
import APILoadingSpinnerRenderer from '../../app/components/apiLoadingSpinner'
import { isAppLoading } from '../../app/selectors'

const mapStyle = {
  width: `${100 - 40}vw`,
  height: `${100 - 63}vw`,
  maxWidth: '100%',
  display: 'block'
}

const DrawMapRenderer = ({
  node,
  setNode,
  setTooltip,
  tooltip,
  viewBoxHeight,
  viewBoxWidth,
  isLoading,
  parentIsLoading
}) => {
  const viewBox = `0 0 ${viewBoxWidth} ${viewBoxHeight}`
  const style = {
    ...mapStyle,
    width: '60vw',
    height: `${(60 / viewBoxWidth) * viewBoxHeight}vw`,
    maxHeight: viewBoxHeight
  }
  return (
    <React.Fragment>
      {isLoading || parentIsLoading ? (
        <APILoadingSpinnerRenderer styles={mapStyle} />
      ) : (
        <React.Fragment>
          <svg
            style={style}
            ref={(newNode) => {
              if (!newNode) {
                return node
              }
              if (newNode === node) {
                return node
              }
              setNode(newNode)
              return newNode
            }}
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            width="100%"
            height={viewBoxHeight}
          />
          <div
            style={{ backgroundColor: colors.backgroundGrey }}
            ref={(newTooltip) => {
              if (!newTooltip) {
                return tooltip
              }
              if (newTooltip === tooltip) {
                return tooltip
              }
              setTooltip(newTooltip)
              return newTooltip
            }}
          />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

DrawMapRenderer.propTypes = {}

const mapStateToProps = (state) => ({ isLoading: isAppLoading(state) })

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch)

const DrawMapComponent = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withState('node', 'setNode', null),
  withState('tooltip', 'setTooltip', null),
  withProps((props) => ({
    tooltipPositionPadding: 20,
    tooltipFN: (tooltip) => select(tooltip)
      .attr('class', 'mapTooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('z-index', '10')
      .style('padding', '20px')
  })),
  withHandlers({
    toolTipLeftPos: (props) => (e) => {
      // if the mouse is more than half way to the right of the chart subtract height of tooltip + gutter
      // ( e.offsetX > (node.getBoundingClientRect().width/2))
      const test = event.offsetX > props.node.getBoundingClientRect().width / 2

      return test
        ? e.clientX - props.tooltip.getBoundingClientRect().width - props.tooltipPositionPadding
        : e.clientX + props.tooltipPositionPadding
    },
    toolTipTopPos: (props) => (e) => {
      // if the mouse is more than half way down the chart subtract height of tooltip + gutter
      const test = event.offsetY > props.node.getBoundingClientRect().height / 2

      return test
        ? e.clientY - props.tooltip.getBoundingClientRect().height + props.tooltipPositionPadding
        : e.clientY + props.tooltipPositionPadding
    }
  }),
  withProps((props) => ({
    drawChoropleth: () => {
      try {
        const node = props.node
        // Check if we have our dom reference yet
        if (!node) {
          return
        }

        const country = props.country || 'US'
        const dataSet = props.mapData
        const feature = JSON.parse(JSON.stringify(GEO_MAP[country].json))
        const geoTransform = JSON.parse(JSON.stringify(GEO_MAP[country].transform))
        const strokeWidth = JSON.parse(JSON.stringify(GEO_MAP[country].strokeWidth))
        const tooltipFN = props.tooltipFN(props.tooltip)
        const path = geoPath().projection(null)
        const svg = select(node)

        const mapFeatures = feature.features
        clearMap(svg)

        svg
          .append('g')
          .attr('transform', geoTransform)
          .attr('class', 'states')
          .selectAll('path')
          .data(mapFeatures)
          .enter()
          .append('path')
          .attr('d', path)
          .style('fill', (d) => props.getFillForState(dataSet, stateAbv(d)))
          .style('stroke', (d) => props.getStrokeForState(dataSet, stateAbv(d)))
          .style('stroke-width', strokeWidth)
          .style('cursor', 'pointer')
          .on('mousedown', (d) => mouseDownEvent(d, props))
          .on('mouseover', (d) => mouseOverEvent(dataSet, d, tooltipFN, props))
          .on('mouseout', (d) => mouseOutEvent(dataSet, d, tooltipFN, props))
          .exit()
          .remove()

        if (country === 'US') {
          const dcData = mapFeatures.find( (feature) => feature.properties.code_hasc === 'US.DC')
          createDCStar(dataSet, dcData, svg, props)
        }

        // Use the gradient to set the shape fill, via CSS.
        if (props.getDomainSpan && dataSet.length > 1) {
          const linear = scaleLinear()
            .domain(props.getDomainSpan) // min max?
            .range([lightBlueRGB, darkBlueRGB])
          // ;
          svg
            .append('g')
            .attr('class', 'legendLinear')
            .attr('transform', 'translate(20,470)')

          const legendLinear = legendColor()
            .shapeWidth(50)
            .cells(7)
            .labelFormat(format(props.ledgendFormat))
            .orient('horizontal')
            .scale(linear)

          svg.select('.legendLinear').call(legendLinear)
        }
      } catch (error) {
        // swallow this for now, map will rerender
        // see ticket ELM-274 for details on how to produce this error
        // https://govdocs.atlassian.net/browse/ELM-274
        // TODO: prevent the error from being thrown in the first place
      }
    },
    viewBoxWidth: 822,
    viewBoxHeight: 500.86
  })),
  lifecycle({
    componentDidMount() {
      this.props.drawChoropleth()
    },
    componentDidUpdate(prevProps, prevState) {
      if (shallowEqual(this.props, prevProps) && shallowEqual(this.state, prevState)) {
        return
      }

      this.props.drawChoropleth()
    }
  })
)(DrawMapRenderer)

export default DrawMapComponent

// Additional Exports Here
export const GEO_MAP = {
  US: {
    json: require('./geoJson/US.geo.json'),
    adminLevel: 'ne_10m_admin_1_states_provinces_lakes',
    name: 'United States',
    transform: 'translate(404, 240) scale(0.00015, -0.00015)',
    strokeWidth: '1000%',
    code: 'US'
  },
  CA: {
    json: require('./geoJson/CA.geo.json'),
    adminLevel: 'ne_10m_admin_1_states_provinces_lakes',
    name: 'Canada',
    transform: 'translate(401, 490) scale(0.0001, -0.0001)',
    strokeWidth: '1000%',
    code: 'CA'
  },
  MX: {
    json: require('./geoJson/MX.geo.json'),
    adminLevel: 'ne_10m_admin_1_states_provinces_lakes',
    name: 'Mexico',
    transform: 'translate(401, 220) scale(0.000137, -0.000137)',
    strokeWidth: '1000%',
    code: 'MX'
  },
  PR: {
    json: require('./geoJson/PR.geo.json'),
    adminLevel: 'ne_10m_admin_1_states_provinces_lakes',
    name: 'Puerto Rico',
    transform: 'translate(-43, 842) scale(0.0025, -0.0025)',
    strokeWidth: '50%',
    code: 'PR'
  },
  GU: {
    json: require('./geoJson/GU.geo.json'),
    adminLevel: 'ne_10m_admin_1_states_provinces_lakes',
    name: 'Guam',
    transform: 'translate(275, 370) scale(0.035, -0.035)',
    strokeWidth: 40,
    code: 'GU'
  },
  VI: {
    json: require('./geoJson/VI.geo.json'),
    adminLevel: 'ne_10m_admin_1_states_provinces_lakes',
    name: 'Virgin Islands',
    transform: 'translate(-860, 1000) scale(0.0035, -0.0035)',
    strokeWidth: '50%',
    code: 'VI'
  }
}

const PROVINCE_NAME_CACHE = {}

Object.keys(GEO_MAP).forEach((key) => {
  // hard-coding for Puerto Rico, Guam, and Virgin Islands
  if (key === 'VI') { // The Virgin Islands has three regions, but they all get the same items so we just use Virgin Islands
    PROVINCE_NAME_CACHE.VI = { name: 'Virgin Islands', postal: 'VI' }
  } else if (key === 'PR') { // Puerto Rico has four regions, but they seem to be identical
    PROVINCE_NAME_CACHE.PR = { name: 'Puerto Rico', postal: 'PR' }
  } else if (key === 'GU') {
    PROVINCE_NAME_CACHE.GU = { name: 'Guam', postal: 'GU' }
  } else {
    const cache = {}
    const geoObj = GEO_MAP[key]
    const geoJson = geoObj.json.features

    geoJson.forEach((area) => {
      const { name, postal } = area.properties
      cache[area.properties.postal] = { name, postal }
    })
    PROVINCE_NAME_CACHE[key] = cache
  }
})

// @todo These need to be filtered by available locations
export const GEO_LIST = _values(_mapValues(GEO_MAP, (v, k) => ({
  ...v,
  code: k
})))

const lightBlueRGB = colors.lightBlue

const darkBlueRGB = colors.govDocsBlue

const mouseDownEvent = (data, props) => {
  if (props.onMapClick) {
    props.onMapClick(data)
  }
}

const mouseOverEvent = (dataSet, data, tooltipFN, props) => {
  const tooltip = props.getTooltipHtml(dataSet, data)
  if (tooltip) {
    tooltipFN
      .style('visibility', 'visible')
      .html(tooltip)
      .style('top', `${props.toolTipTopPos(event)}px`)
      .style('left', `${props.toolTipLeftPos(event)}px`)
  }
}


const mouseOutEvent = (dataSet, data, tooltipFN, props) => {
  tooltipFN.style('visibility', 'hidden').html(props.getTooltipHtml(dataSet, data))
}

const createDCStar = (dataSet, data, svg, props) => {
  const tooltipFN = props.tooltipFN(props.tooltip)
  svg
    .append('polygon')
    .attr('transform', 'translate(620,175) scale(0.08)')
    .attr('points', '350,75  379,161 469,161 397,215,423,301 350,250 277,301 303,215,231,161 321,161')
    .style('fill', () => props.getFillForState(dataSet, stateAbv(data)))
    .style('stroke', () => props.getStrokeForState(dataSet, stateAbv(data)))
    .style('stroke-width', '15px')
    .style('cursor', 'pointer')
    .on('mousedown', () => mouseDownEvent(data, props))
    .on('mouseover', () => mouseOverEvent(dataSet, data, tooltipFN, props))
    .on('mouseout', () => mouseOutEvent(dataSet, data, tooltipFN, props))
    .exit()
    .remove()
}

const clearMap = (svg) => {
  svg.selectAll('g').remove()
  svg.selectAll('polygon').remove()
}

export const stateAbv = (d) => {
  if (d && d.properties) {
    if (d.properties.postal) {
      return d.properties.postal
    }

    if (d.properties.hasc) {
      return d.properties.hasc
    }

    if (_get(d.properties, 'sr_subunit') === 'Puerto Rico') {
      return 'PR'
    }

    return d.properties.VARNAME_1.split('|')[0]
  }

  return null
}

export const stateName = (d) => {
  if (d && d.properties) {
    if (d.properties.name) {
      return d.properties.name
    }

    return d.properties.NAME_1
  }

  return null
}

export const stateNameByAbv = (state, countryCode) => _get(PROVINCE_NAME_CACHE[countryCode][state], 'name') || 'N/A'

export const stateOrCountryNameByAbv = (state, country) => _get(PROVINCE_NAME_CACHE[country.code][state], 'name') || country.name

export const getFillByWage = (dataSet, abv) => {
  const mapWageMax = dataSet.reduce((highestWage, nextWageObject) => (
    highestWage > nextWageObject.wageAmount ? highestWage : nextWageObject.wageAmount), 0)

  const mapWageMin = dataSet.reduce((lowestWage, nextWageObject) => {
    return nextWageObject.wageAmount && nextWageObject.wageAmount < lowestWage
      ? nextWageObject.wageAmount
      : lowestWage
  }, Number.MAX_VALUE)

  const wageObj = dataSet.filter((x) => x.stateAbv === abv)
  if (wageObj.length === 0) return lightBlueRGB
  const wageAmount = wageObj[0].wageAmount || mapWageMin
  const rangeValue = ((wageAmount - mapWageMin) * 100) / (mapWageMax - mapWageMin) / 100
  return interpolateRgb(lightBlueRGB, darkBlueRGB)(rangeValue)
}

export const getFillByCount = (dataSet, abv) => {
  const locationObj = dataSet.filter((x) => x.stateAbv === abv)
  if (locationObj.length === 0) return colors.white
  const maxLocations = dataSet.sort((a, b) => parseFloat(b.count) - parseFloat(a.count))[0].count
  const locationCount = locationObj[0].count || 1
  const rangeValue = locationCount / maxLocations
  return interpolateRgb(lightBlueRGB, darkBlueRGB)(rangeValue)
}

export const getFillByPaidLeave = (dataSet, abv) => {
  const stateObject = dataSet.find((state) => state.stateAbv === abv)
  return stateObject.hasProducts
    ? darkBlueRGB
    : lightBlueRGB
}

export const getStrokeByCount = (dataSet, abv) => {
  const fillColor = getFillByCount(dataSet, abv)
  return d3Color(fillColor).darker()
}

export const getStrokeByPaidLeave = (dataSet, abv) => {
  const fillColor = getFillByPaidLeave(dataSet, abv)
  return d3Color(fillColor).darker()
}

export const getStrokeByWage = (dataSet, abv) => {
  // Just use a darker contrast color based on fill
  const fillColor = getFillByWage(dataSet, abv)
  return d3Color(fillColor).darker()
}

export const getWageAmountFromAbv = (dataSet, abv) => {
  let wageObj = dataSet.filter((x) => x.stateAbv === abv)
  if (wageObj.length === 0) {
    wageObj = dataSet.filter((x) => x.stateAbv === 'federal')
  }
  return parseFloat(wageObj[0].wageAmount)
}

export const getHasMultiple = (dataSet, abv) => {
  const wageObj = dataSet.filter((x) => x.stateAbv === abv)
  if (wageObj.length === 0) return 'N/A'
  return wageObj[0].hasMultiple
}

export const tooltipTextWage = (dataSet, datum) => {
  const abv = stateAbv(datum)
  const name = stateName(datum)
  const wageAmount = getWageAmountFromAbv(dataSet, abv)
  const wageString = `$${wageAmount.toFixed(2)}`
  const hasMultiple = getHasMultiple(dataSet, abv)
  const federalWage = getWageAmountFromAbv(dataSet, 'federal')
  const fedString = `$${federalWage.toFixed(2)}`

  const wage = (!Number.isNaN(wageAmount) && wageAmount >= federalWage)
    ? `<p style="margin-bottom: 0px">Wage Amount: ${wageString}</p>`
    : `<p style="margin-bottom: 0px">Wage Amount: ${fedString}</p>`

  let multiple = ''
  if (hasMultiple === true) {
    multiple = 'Multiple rates applicable'
  }

  return `
    <p style="margin-top: 0px;font-weight: 600;">
        ${name}
    </p>
    ${wage}
    ${multiple}
  `
}

export const getDomainBy = (dataSet, property) => {
  // return an array with 2 numbers
  if (dataSet && dataSet.length !== 0) {
    const filtered = _filter(dataSet, (x) => x[property])
    const sorted = _sortBy(filtered, [property])
    const min = sorted[0]
    const max = sorted.reverse()[0]
    return sorted.length !== 0
      ? sorted[0].hasOwnProperty(property)
        ? [min[property], max[property]]
        : [0, 0]
      : [0, 0]
  }
  return [0, 0]
}
